import { useState, useCallback, useRef, useEffect } from 'react';

const CACHE_UPDATE_INTERVAL = 1000;

export const useElementCache = (selector, enabled) => {
  const [elements, setElements] = useState([]);
  const cacheUpdateIntervalRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);

  const updateCache = useCallback(() => {
    if (!enabled) {
      setElements([]);
      return;
    }

    const now = Date.now();

    if (now - lastUpdateTimeRef.current < 100) return;

    lastUpdateTimeRef.current = now;

    try {
      const gradientElements = document.querySelectorAll(selector);

      const elementData = Array.from(gradientElements)
        .map((element) => {
          const rect = element.getBoundingClientRect();
          return {
            element,
            width: rect.width,
            height: rect.height,
            id: element.dataset.gradientId || element.className
          };
        })
        .filter(({ width, height }) => width > 0 && height > 0);

      setElements(elementData);
    } catch (error) {
      setElements([]);
    }
  }, [selector, enabled]);

  useEffect(() => {
    if (!enabled) {
      if (cacheUpdateIntervalRef.current) {
        clearInterval(cacheUpdateIntervalRef.current);
        cacheUpdateIntervalRef.current = null;
      }
      return;
    }

    updateCache();

    cacheUpdateIntervalRef.current = setInterval(
      updateCache,
      CACHE_UPDATE_INTERVAL
    );

    return () => {
      if (cacheUpdateIntervalRef.current) {
        clearInterval(cacheUpdateIntervalRef.current);
      }
    };
  }, [enabled, updateCache]);

  useEffect(() => {
    if (!enabled) return;

    const handleResize = () => {
      updateCache();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [enabled, updateCache]);

  return {
    elements,
    updateCache
  };
};
